import React, { useState, useEffect } from 'react'
import DataTable from 'react-data-table-component'
import CustomPagination from '../CustomPagination/CustomPagination';
import * as Icons from "../../data/teacher";
import SkeletonTable from '../common/SkeletonTable';
import NoDataComponent from '../common/NoDataComponent';
import { useStateContext } from '../../contexts/ContextProvider';
import { generateSchoolSessions } from '../../utils/schoolYearUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import emmymaxLogo from '../../data/logo/logo.png';
import { MdWbSunny, MdCloud } from 'react-icons/md';

const customStyles = {
    headRow: {
        style: {
            backgroundColor: 'rgb(248, 250, 252)',
            fontWeight: "bold",
        },
    },
};

const StudentAttendance = ({ studentAttendance, loading, onAttendanceView, school, studentId, classId, studentName }) => {
    const { user } = useStateContext();
    const schoolTerm = school?.settings?.current_term;
    const schoolSession = school?.settings?.current_session;

    // Term and Session filter states
    const [termDropdown, setTermDropdown] = useState(false);
    const [sessionDropdown, setSessionDropdown] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('studentAttendanceSelectedTerm'));
            if (saved && typeof saved === 'object' && saved.id && saved.name) {
                return { id: String(saved.id), name: String(saved.name) };
            }
        } catch (e) {
            // Invalid JSON, ignore
        }
        return { id: String(schoolTerm || '1'), name: `Term ${schoolTerm || '1'}` };
    });
    const [selectedSession, setSelectedSession] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('studentAttendanceSelectedSession'));
            if (saved && typeof saved === 'object') {
                const sessionId = saved.id || saved.value || '';
                const sessionName = saved.name || saved.label || sessionId;
                if (sessionId) {
                    return { id: String(sessionId), name: String(sessionName) };
                }
            }
        } catch (e) {
            // Invalid JSON, ignore
        }
        const defaultSession = String(schoolSession || '2024/2025');
        return { id: defaultSession, name: defaultSession };
    });

    // Generate school sessions
    const schoolSessionsData = generateSchoolSessions(2020, 10);
    const schoolSessions = schoolSessionsData.map(s => typeof s === 'string' ? s : (s.value || s.label || s));

    // Get available terms from school settings
    const availableTerms = school?.settings?.terms?.map(term => {
        const termId = typeof term === 'object' && term.term ? String(term.term) : String(term || '1');
        return {
            id: termId,
            name: `Term ${termId}`
        };
    }).filter(term => term.id && term.name) || [
        { id: '1', name: 'Term 1' },
        { id: '2', name: 'Term 2' },
        { id: '3', name: 'Term 3' }
    ];

    const handleTermDropdown = () => setTermDropdown(!termDropdown);
    const handleSessionDropdown = () => setSessionDropdown(!sessionDropdown);

    const handleTermSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedTerm(newSelection);
        localStorage.setItem('studentAttendanceSelectedTerm', JSON.stringify(newSelection));
        setTermDropdown(false);
        // Trigger attendance fetch with new term
        if (onAttendanceView && typeof onAttendanceView === 'function') {
            onAttendanceView(id, selectedSession.id);
        }
    };

    const handleSessionSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedSession(newSelection);
        localStorage.setItem('studentAttendanceSelectedSession', JSON.stringify(newSelection));
        setSessionDropdown(false);
        // Trigger attendance fetch with new session
        if (onAttendanceView && typeof onAttendanceView === 'function') {
            onAttendanceView(selectedTerm.id, id);
        }
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (termDropdown || sessionDropdown) {
                if (!event.target.closest('.filter-dropdown')) {
                    setTermDropdown(false);
                    setSessionDropdown(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [termDropdown, sessionDropdown]);

    // Initial fetch with selected term and session when component mounts
    useEffect(() => {
        if (onAttendanceView && typeof onAttendanceView === 'function' && selectedTerm.id && selectedSession.id && school?._id) {
            onAttendanceView(selectedTerm.id, selectedSession.id);
        }
    }, [school?._id]); // Run when school is available

    const [selectedPeriod, setSelectedPeriod] = useState('morning');
    const [exporting, setExporting] = useState(false);

    // Export student attendance to PDF — shows both AM and PM columns
    const exportStudentAttendanceToPDF = async () => {
        if (!studentAttendance || studentAttendance.length === 0) {
            alert('No attendance data available to export');
            return;
        }

        setExporting(true);

        try {
            const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for 11 data columns
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15;
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
            const statusLabel = (s) => s === 1 ? 'P' : s === 2 ? 'A' : '-';

            const addPageHeader = (pageNum) => {
                const headerY = 10;
                try {
                    const logoSource = school?.picture?.path || emmymaxLogo;
                    doc.addImage(logoSource, logoSource.includes('http') ? 'PNG' : 'JPG', margin, headerY, 25, 18);
                } catch (e) {}

                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(school?.school_name || 'School Name', margin + 30, headerY + 6);

                doc.setFontSize(11);
                doc.text(`Student: ${studentName || 'N/A'}`, margin + 30, headerY + 12);

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.text(`Term: ${selectedTerm?.name || 'N/A'} | Session: ${selectedSession?.name || 'N/A'}`, margin + 30, headerY + 17);

                doc.setFontSize(13);
                doc.setFont('helvetica', 'bold');
                doc.text('Attendance Report', pageWidth / 2, headerY + 24, { align: 'center' });

                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(`Page ${pageNum}`, pageWidth - margin, headerY + 5, { align: 'right' });
                if (pageNum === 1) {
                    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth - margin, headerY + 10, { align: 'right' });
                }
            };

            const sortedWeeks = [...studentAttendance].sort((a, b) => a.week - b.week);

            // Headers: Week | Mon AM | Mon PM | Tue AM | Tue PM | ... | AM Rate
            const headers = ['Week'];
            dayNames.forEach(d => { headers.push(`${d} AM`); headers.push(`${d} PM`); });
            headers.push('AM Rate');

            const tableData = sortedWeeks.map(weekData => {
                const row = [`Week ${weekData.week}`];
                let amPresent = 0, amTotal = 0;

                dayNames.forEach((_, i) => {
                    const dow = i + 1;
                    const amDay = weekData.days.find(d => d.day_of_week === dow && (d.period || 'morning') === 'morning');
                    const pmDay = weekData.days.find(d => d.day_of_week === dow && d.period === 'afternoon');
                    row.push(amDay ? statusLabel(amDay.status) : '-');
                    row.push(pmDay ? statusLabel(pmDay.status) : '-');
                    if (amDay) { amTotal++; if (amDay.status === 1) amPresent++; }
                });

                const rate = amTotal > 0 ? Math.round((amPresent / amTotal) * 100) : 0;
                row.push(`${rate}%`);
                return row;
            });

            // Column widths
            const availableWidth = pageWidth - margin * 2;
            const weekW = 20;
            const rateW = 18;
            const dayW = (availableWidth - weekW - rateW) / 10; // 5 days × 2 periods

            const columnStyles = { 0: { cellWidth: weekW, fontStyle: 'bold', halign: 'left' } };
            for (let i = 1; i <= 10; i++) columnStyles[i] = { cellWidth: dayW, halign: 'center' };
            columnStyles[11] = { cellWidth: rateW, halign: 'center', fontStyle: 'bold' };

            addPageHeader(1);

            doc.autoTable({
                head: [headers],
                body: tableData,
                startY: 40,
                margin: { left: margin, right: margin, top: 40 },
                styles: { fontSize: 7.5, cellPadding: 1.5, lineWidth: 0.1 },
                headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold', fontSize: 7.5, halign: 'center', cellPadding: 2 },
                columnStyles,
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    const currentPageNum = doc.internal.getCurrentPageInfo().pageNumber;
                    if (currentPageNum > 1) addPageHeader(currentPageNum);
                },
            });

            // Overall stats (AM-based rate)
            let amPresent = 0, amAbsent = 0, pmPresent = 0, pmAbsent = 0;
            sortedWeeks.forEach(weekData => {
                weekData.days.forEach(d => {
                    const period = d.period || 'morning';
                    if (period === 'morning') {
                        if (d.status === 1) amPresent++; else if (d.status === 2) amAbsent++;
                    } else if (period === 'afternoon') {
                        if (d.status === 1) pmPresent++; else if (d.status === 2) pmAbsent++;
                    }
                });
            });
            const amTotal = amPresent + amAbsent;
            const overallRate = amTotal > 0 ? Math.round((amPresent / amTotal) * 100) : 0;

            const finalY = doc.lastAutoTable.finalY + 5;
            const writeSummary = (y) => {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.text('Summary:', margin, y);
                doc.setFont('helvetica', 'normal');
                doc.text(
                    `Morning — Present: ${amPresent}, Absent: ${amAbsent}, Rate: ${overallRate}% | Afternoon — Present: ${pmPresent}, Absent: ${pmAbsent}`,
                    margin + 20, y
                );
                doc.setFont('helvetica', 'bold');
                doc.text('Legend:', margin, y + 6);
                doc.setFont('helvetica', 'normal');
                doc.text('P = Present | A = Absent | - = Not Marked | AM Rate is based on morning attendance', margin + 18, y + 6);
            };

            if (pageHeight - finalY > 20) {
                writeSummary(finalY);
            } else {
                doc.addPage();
                addPageHeader(doc.internal.getCurrentPageInfo().pageNumber);
                writeSummary(35);
            }

            const fileName = `${(studentName || 'Student').replace(/[^a-zA-Z0-9]/g, '_')}_Attendance_${selectedTerm?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'term'}_${selectedSession?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'session'}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const findDay = (row, dayOfWeek) =>
        row.days.find(d => d.day_of_week === dayOfWeek && (d.period || 'morning') === selectedPeriod);

    const dayIcon = (day) => {
        if (!day) return <img src={Icons.studentPending} alt="Pending Icon" />;
        if (day.status === 1) return <img src={Icons.studentPresent} alt="Present Icon" />;
        return (
            <div className="relative inline-flex items-center justify-center" title={day.reason || 'Absent'}>
                <img src={Icons.studentAbsent} alt="Absent Icon" />
                {day.reason && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-400 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold" style={{ fontSize: '7px' }}>!</span>
                    </span>
                )}
            </div>
        );
    };

    const SHORT_DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

    const getAbsentWithReasons = (row) =>
        row.days
            .filter(d => (d.period || 'morning') === selectedPeriod && d.status === 2 && d.reason)
            .sort((a, b) => a.day_of_week - b.day_of_week);

    const ExpandedReasons = ({ data: row }) => {
        const absent = getAbsentWithReasons(row);
        return (
            <div className="px-6 py-4 bg-red-50 border-t border-red-100">
                <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-3">
                    Absence reasons — Week {row.week}
                </p>
                <div className="flex flex-wrap gap-2">
                    {absent.map(d => (
                        <div key={d._id || d.day_of_week} className="flex items-center gap-2 bg-white border border-red-200 rounded-xl px-3 py-2 shadow-sm">
                            <span className="text-xs font-bold text-red-500">{SHORT_DAY_NAMES[d.day_of_week - 1]}</span>
                            <span className="text-gray-300 text-xs">|</span>
                            <span className="text-xs text-gray-700">{d.reason}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const columns = [
        { name: "Week", selector: row => row.week },
        { name: "Monday",    cell: row => dayIcon(findDay(row, 1)) },
        { name: "Tuesday",   cell: row => dayIcon(findDay(row, 2)) },
        { name: "Wednesday", cell: row => dayIcon(findDay(row, 3)) },
        { name: "Thursday",  cell: row => dayIcon(findDay(row, 4)) },
        { name: "Friday",    cell: row => dayIcon(findDay(row, 5)) },
        {
            name: "Attendance Rate",
            cell: row => {
                const pd = row.days.filter(d => (d.period || 'morning') === selectedPeriod);
                const rate = pd.length > 0 ? Math.round((pd.filter(d => d.status === 1).length / pd.length) * 100) : 0;
                return <p className="text-[#3DCE3D] font-semibold">{rate}%</p>;
            },
        },
    ];

  return (
    <div>
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-2xl font-semibold">Attendance</p>
            <button
                onClick={exportStudentAttendanceToPDF}
                disabled={exporting || !studentAttendance || studentAttendance.length === 0}
                className="flex items-center gap-2 hover:bg-indigo-900 rounded-full text-sm text-white px-4 py-2 bg-[#3F3DA5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
        </div>

        {/* Controls bar: period toggle + term/session */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 p-3 bg-gray-50 rounded-2xl border border-gray-100">
            {/* Period toggle */}
            <div className="flex bg-white rounded-xl p-1 gap-1 shadow-sm border border-gray-100">
                <button
                    onClick={() => setSelectedPeriod('morning')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                        selectedPeriod === 'morning'
                            ? 'bg-amber-500 text-white shadow-sm'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                >
                    <MdWbSunny className={`text-base ${selectedPeriod === 'morning' ? 'text-white' : 'text-amber-400'}`} />
                    Morning
                </button>
                <button
                    onClick={() => setSelectedPeriod('afternoon')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                        selectedPeriod === 'afternoon'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                >
                    <MdCloud className={`text-base ${selectedPeriod === 'afternoon' ? 'text-white' : 'text-blue-400'}`} />
                    Afternoon
                </button>
            </div>

            {/* Term & Session dropdowns */}
            <div className="flex flex-wrap items-center gap-2 filter-dropdown">
                <div className="relative text-[#4543B5] text-xs">
                    <button
                        type="button"
                        onClick={handleTermDropdown}
                        className="flex items-center gap-1.5 text-sm font-semibold bg-white hover:bg-blue-50 border border-[#C5C5E8] py-2 px-3 rounded-full transition-colors"
                    >
                        <span>{typeof selectedTerm?.name === 'string' ? selectedTerm.name : `Term ${selectedTerm?.id || schoolTerm || '1'}`}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6l6 -6" /></svg>
                    </button>
                    {termDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-max max-h-52 overflow-y-auto">
                            {availableTerms.map(term => {
                                const termName = typeof term.name === 'string' ? term.name : `Term ${term.id || '1'}`;
                                return (
                                    <div key={term.id} onClick={() => handleTermSelection(String(term.id), termName)} className="cursor-pointer text-sm px-4 py-2.5 hover:bg-blue-50 first:rounded-t-xl last:rounded-b-xl">
                                        {termName}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="relative text-[#4543B5] text-xs">
                    <button
                        type="button"
                        onClick={handleSessionDropdown}
                        className="flex items-center gap-1.5 text-sm font-semibold bg-white hover:bg-blue-50 border border-[#C5C5E8] py-2 px-3 rounded-full transition-colors"
                    >
                        <span>{typeof selectedSession?.name === 'string' ? selectedSession.name : (selectedSession?.id || schoolSession || '2024/2025')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6l6 -6" /></svg>
                    </button>
                    {sessionDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-max max-h-52 overflow-y-auto">
                            {schoolSessions.map(session => {
                                const sessionStr = typeof session === 'string' ? session : String(session || '');
                                return (
                                    <div key={sessionStr} onClick={() => handleSessionSelection(sessionStr, sessionStr)} className="cursor-pointer text-sm px-4 py-2.5 hover:bg-blue-50 first:rounded-t-xl last:rounded-b-xl">
                                        {sessionStr}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
        <div className="border rounded-3xl">
            <DataTable
                columns={columns}
                data={studentAttendance}
                noDataComponent={<NoDataComponent/>}
                expandableRows
                expandableRowsComponent={ExpandedReasons}
                expandableRowDisabled={row => getAbsentWithReasons(row).length === 0}
                pagination
                paginationComponent={CustomPagination}
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                customStyles={customStyles}
                progressPending={loading}
                progressComponent={<SkeletonTable rows={5} columns={6} />}
                highlightOnHover
                striped
            />
        </div>
    </div>
  )
}

export default StudentAttendance